import { Type } from '@sinclair/typebox';
import { TfUserSchema } from './tables.js';
// Hub identity surface (Phase 1).
//
// All auth endpoints live under `/auth/*` and `/.well-known/*` on the hub.
// Schemas are shared so both the hub server (Fastify route declarations) and
// the hub-client (typed endpoint registry) reference the same shapes.
const PasswordSchema = Type.String({ minLength: 8, maxLength: 256 });
const EmailSchema = Type.String({ format: 'email', maxLength: 320 });
// POST /auth/register
export const RegisterBodySchema = Type.Object({
    email: EmailSchema,
    password: PasswordSchema,
    name: Type.Optional(Type.String({ maxLength: 200 })),
});
// POST /auth/login
export const LoginBodySchema = Type.Object({
    email: EmailSchema,
    password: PasswordSchema,
});
// POST /auth/refresh
//
// `refreshToken` is optional: cookie-based callers (browsers carrying
// tf_refresh) post an empty `{}`, server-side legacy SDKs that hold the token
// in memory pass it explicitly. The route reads whichever it gets.
export const RefreshBodySchema = Type.Object({
    refreshToken: Type.Optional(Type.String()),
});
// POST /auth/logout
//
// `refreshToken` is optional. Cookie/Bearer-authed callers don't need to
// supply it — the route revokes every refresh family for the authenticated
// user. Anonymous callers (no JWT) may pass a token to revoke just that one.
export const LogoutBodySchema = Type.Object({
    refreshToken: Type.Optional(Type.String()),
});
// POST /auth/password/change
export const ChangePasswordBodySchema = Type.Object({
    currentPassword: PasswordSchema,
    newPassword: PasswordSchema,
});
// Token-pair response (login, register, refresh)
export const TokenPairResponseSchema = Type.Object({
    accessToken: Type.String(),
    refreshToken: Type.String(),
    tokenType: Type.Literal('Bearer'),
    expiresIn: Type.Integer(),
    user: TfUserSchema,
});
// GET /auth/me
export const MeResponseSchema = Type.Object({
    user: TfUserSchema,
});
// POST /auth/logout, POST /auth/password/change → 204 No Content (no body schema needed)
// --- Phase 3: identity extras ---
// POST /auth/verify-email/request — body is empty; route uses request.user.
// We still declare a schema (Type.Object({})) so the global onRoute hook is
// happy and the route stays self-documenting.
export const RequestVerifyEmailBodySchema = Type.Object({});
// POST /auth/verify-email/confirm
export const ConfirmVerifyEmailBodySchema = Type.Object({
    token: Type.String({ minLength: 8, maxLength: 256 }),
});
// POST /auth/password-reset/request
//
// Always 204 regardless of whether the email is recognized. The body shape is
// intentionally narrow so anti-enumeration logic on the server doesn't hinge
// on schema-level branching.
export const RequestPasswordResetBodySchema = Type.Object({
    email: EmailSchema,
});
// POST /auth/password-reset/confirm
export const ConfirmPasswordResetBodySchema = Type.Object({
    token: Type.String({ minLength: 8, maxLength: 256 }),
    newPassword: PasswordSchema,
});
// --- OAuth: Google / GitHub / Microsoft Entra ID ---
// Path param `:provider` for /auth/oauth/:provider/start and /callback. We
// validate at the route layer to keep error shape consistent (400, not 404).
export const OauthProviderParamsSchema = Type.Object({
    provider: Type.Union([Type.Literal('google'), Type.Literal('github'), Type.Literal('microsoft')]),
});
/**
 * Button label per provider — "Sign in with <label>". ONE map for every login
 * surface (the hub's /login portal and the admin SPA's login page) so a new
 * provider cannot show up capitalised on one page and raw on the other.
 */
export const OAUTH_PROVIDER_LABELS = {
    google: 'Google',
    github: 'GitHub',
    microsoft: 'Microsoft',
};
// GET /auth/oauth/:provider/start — query controls where to bounce back to
// after the flow completes. Accept relative paths only (validated server-side
// so the hub can't be turned into an open-redirect). When `link=1` and the
// caller already holds a Bearer token, the resulting account is linked to the
// existing user instead of logging in.
export const OauthStartQuerySchema = Type.Object({
    redirectTo: Type.Optional(Type.String({ maxLength: 1024 })),
    link: Type.Optional(Type.Union([Type.Literal('0'), Type.Literal('1')])),
});
export const OauthStartResponseSchema = Type.Object({
    /** Fully-qualified URL the browser should redirect to. */
    authorizationUrl: Type.String(),
    /** Echoed back so SPA callers can store it themselves if they need to. */
    state: Type.String(),
});
// GET /auth/oauth/:provider/callback — the provider returns `code` + `state`.
// On error the provider may return `error` / `error_description` instead.
export const OauthCallbackQuerySchema = Type.Object({
    code: Type.Optional(Type.String()),
    state: Type.Optional(Type.String()),
    error: Type.Optional(Type.String()),
    error_description: Type.Optional(Type.String()),
});
// GET /auth/oauth/accounts → linked-account list for the current user.
export const OauthAccountSchema = Type.Object({
    provider: Type.String(),
    providerAccountId: Type.String(),
    email: Type.Union([Type.String(), Type.Null()]),
    emailVerified: Type.Boolean(),
    createdAt: Type.String(),
});
export const OauthAccountsResponseSchema = Type.Object({
    accounts: Type.Array(OauthAccountSchema),
});
// GET /.well-known/jwks.json
//
// Per RFC 7517, the JWK Set is `{ keys: [JWK, ...] }`. JWK fields are not
// fully enumerated in our schema — they vary per algorithm — so we accept
// a permissive object shape and let consumers drill in.
export const JwksResponseSchema = Type.Object({
    keys: Type.Array(Type.Record(Type.String(), Type.Unknown())),
});
// GET /.well-known/openid-configuration — minimal subset that lets generic
// JWT consumers (e.g. @fastify/jwt + jwks-rsa) discover the JWKS endpoint.
export const OpenIdConfigurationResponseSchema = Type.Object({
    issuer: Type.String(),
    jwks_uri: Type.String(),
    id_token_signing_alg_values_supported: Type.Array(Type.String()),
    response_types_supported: Type.Array(Type.String()),
    subject_types_supported: Type.Array(Type.String()),
});
//# sourceMappingURL=auth.js.map